/**
 * PlayerPageHeaderContainer - Main container for the player page header
 *
 * Orchestrates the player header display including:
 * - Feature flag gating
 * - Loading state handling
 * - ChartCaptureProvider wrapping for PDF generation
 * @remarks This is a Container component - handles all business logic
 */

import { useCanAccessFeature } from "@/providers/FeatureFlags";
import { useTheme } from "@/providers/ThemeProvider";

import { ChartCaptureProvider } from "@/features/player-detail/providers/ChartCaptureProvider";
import type { PlayerPageHeaderContainerProps } from "@/features/player-detail/utils/playerPageHeaderTypes";
import { PLAYER_PAGE_HEADER_FEATURE_FLAG } from "@/features/player-detail/utils/playerPageHeaderTypes";
import PlayerPageHeaderInner from "@/features/player-detail/components/organisms/PlayerPageHeaderInner";

/**
 * PlayerPageHeaderContainer - Wraps inner component with ChartCaptureProvider
 * @param props - Container props including player fragment and loading state
 * @param props.player - Player data fragment
 * @param props.loading - Whether player data is loading
 * @param props.isModal - Whether rendered inside a modal for compact layout
 * @returns Feature-gated header with loading skeleton and indicator modal
 */
const PlayerPageHeaderContainer = ({
  player,
  loading,
  isModal = false,
}: PlayerPageHeaderContainerProps) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  // Feature flag check - temporarily hardcoded to true for development
  const _isFeatureEnabled = useCanAccessFeature(
    PLAYER_PAGE_HEADER_FEATURE_FLAG
  );
  const isFeatureEnabled = true;

  if (!isFeatureEnabled) {
    return null;
  }

  return (
    <ChartCaptureProvider>
      <PlayerPageHeaderInner
        player={player}
        loading={loading}
        isDark={isDark}
        isModal={isModal}
      />
    </ChartCaptureProvider>
  );
};

PlayerPageHeaderContainer.displayName = "PlayerPageHeaderContainer";

export default PlayerPageHeaderContainer;
